import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/LoadingSpinner';
import { ApiService } from '../utils/api';
import { formatDate, parseBackendDate } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, 
  Users, 
  QrCode, 
  Plus, 
  Eye, 
  TrendingUp,
  Clock,
  AlertCircle,
  Activity,
  BarChart3
} from 'lucide-react';
import { eventRef } from '../utils/eventRef';

const AdminDashboard = () => {
  const { user, isAdmin, isEditor, isViewer } = useAuth();

  // Check if user is VIEWER only (not EDITOR or ADMIN)
  const isViewerOnly = isViewer() && !isEditor() && !isAdmin();

  // Fetch events data from API
  const { data: eventsResponse, isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: ['events'],
    queryFn: () => ApiService.getEvents(0, 10), // Get first 10 events
    enabled: !!user, // Only fetch if user is authenticated
  });

  // Extract events from paginated response
  const events = eventsResponse?.content || [];

  // Recent Events lists the events with the latest registrations or changes first.
  const { data: recentEvents = [] } = useQuery({
    queryKey: ['events', 'recent'],
    queryFn: () => ApiService.getRecentEvents(8),
    enabled: !!user && isAdmin(),
  });



  // Fetch current user data for statistics
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => ApiService.getCurrentUser(),
    enabled: !!user,
  });

  // Fetch recent activity/audit logs (if available)
  const { data: recentActivity = [] } = useQuery({
    queryKey: ['recentActivity'],
    queryFn: async () => {
      // For now, we'll generate activity based on events
      // In a real implementation, this would fetch from an audit log API
      const activity = [];
      events.forEach((event, index) => {
        activity.push({
          id: `activity_${event.id}_${index}`,
          type: index === 0 ? 'registration' : index === 1 ? 'scan' : 'event_created',
          message: index === 0 ? `New registration for ${event.name}` :
                   index === 1 ? `QR code scanned for ${event.name}` :
                   `${event.name} created`,
          timestamp: new Date(Date.now() - (index + 1) * 15 * 60 * 1000).toISOString(), // 15 min intervals
          eventId: event.id,
          eventName: event.name
        });
      });
      return activity;
    },
    enabled: !!events.length,
  });


  // Calculate statistics from real data
  const totalRegistrations = events.reduce((sum, event) => sum + (event.registered || 0), 0);
  const upcomingEvents = events.filter(event => {
    if (!event.eventDate) return false;
    return new Date(event.eventDate) > new Date();
  }).length;

  // Get QR scans from user data
  const qrScansToday = currentUser?.qrScansToday || 0;

  // Loading state
  const isLoading = eventsLoading || userLoading;

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Loading dashboard..." />;
  }

  // Viewer-only staff get the simplified desk (on-spot registration + badge printing).
  if (isViewerOnly) {
    return <Navigate to={events.length === 1 ? `/desk/${events[0].id}` : '/desk'} replace />;
  }

  // Non-admin users: redirect based on available events
  const isAdminUser = isAdmin();
  if (!isAdminUser) {
    if (events.length === 1) {
      // If exactly one event exists, send directly to its registrations page
      return (
        <Navigate
          to={`/admin/events/${events[0].id}/registrations`}
          replace
        />
      );
    }
    // Otherwise, fall back to the events list
    return <Navigate to="/admin/events" replace />;
  }

  // Handle error state
  if (eventsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 mb-4">
              {eventsError.message || 'Failed to load dashboard data. Please try again.'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = parseBackendDate(timestamp);
    if (!time) return '';
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">
            {isViewerOnly 
              ? "View events and generate QR scan links" 
              : "Welcome back! Here's what's happening with your events."}
          </p>
        </div>
        {!isViewerOnly && (
          <div className="flex gap-2">
            <Link to="/admin/events" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </Link>
          </div>
        )}
      </div>


      {/* Stats Cards */}
      {!isViewerOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Events</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{events.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Registrations</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalRegistrations}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Upcoming Events</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{upcomingEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <QrCode className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">QR Scans Today</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{qrScansToday}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Events */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Events</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
              <p className="text-gray-600 mb-4">
                {isViewerOnly 
                  ? "No events available to view at the moment." 
                  : "Create your first event to get started."}
              </p>
              {!isViewerOnly && (
                <Link to="/admin/events">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Event
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {recentEvents.map((event) => (
                <div key={event.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{event.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500">
                      {event.eventDate && (
                        <span className="flex items-center">
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          {formatDate(event.eventDate)}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Users className="h-3.5 w-3.5 mr-1" />
                        {(event.registered || 0).toLocaleString('en-IN')} registered
                      </span>
                      {event.lastActivityAt && (
                        <span className="flex items-center">
                          <Clock className="h-3.5 w-3.5 mr-1" />
                          Last activity {formatTimeAgo(event.lastActivityAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link to={`/admin/events/${eventRef(event)}/registrations`} className="flex-1 sm:flex-none">
                      <Button variant="outline" size="sm" className="w-full">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </Link>
                    <Link to={`/admin/events/${eventRef(event)}/analytics`} className="flex-1 sm:flex-none">
                      <Button variant="outline" size="sm" className="w-full">
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Analytics
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/scanner" className="block">
              <Button variant="outline" className="w-full justify-start">
                <QrCode className="h-4 w-4 mr-2" />
                Scan QR Code
              </Button>
            </Link>
            <Link to="/admin/events" className="block">
              <Button variant="outline" className="w-full justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Manage Events
              </Button>
            </Link>
            {events.length > 0 && (
              <Link to={`/admin/events/${events[0].id}/team`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  Team Management
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                <>
                  {recentActivity.slice(0, 3).map((activity) => (
                    <div key={activity.id} className="flex items-center text-sm">
                      <div className={`w-2 h-2 rounded-full mr-3 ${
                        activity.type === 'registration' ? 'bg-green-500' : 
                        activity.type === 'scan' ? 'bg-blue-500' : 'bg-purple-500'
                      }`}></div>
                      <span className="text-gray-600">{activity.message}</span>
                      <span className="text-gray-400 ml-auto">
                        {formatTimeAgo(activity.timestamp)}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-4">
                  <Activity className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No recent activity</p>
                  <p className="text-xs text-gray-400">Activity will appear here as you use the platform</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div> */}

    </div>
  );
};

export default AdminDashboard;
